import { LoaderCircle, Sparkles, SwatchBook, TriangleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ArtstyleReferenceManager } from "@/components/image-generation/artstyle-reference-manager";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FormMessage } from "@/components/ui/form-message";
import { Label } from "@/components/ui/label";
import type {
  ProjectImageSettingsDraft,
  ProjectImageSettingsView,
} from "@/features/image-generation";
import { cn } from "@/lib/utils";

const TEXTAREA_CLASS_NAME =
  "flex min-h-[10rem] w-full rounded-[24px] border border-input bg-background/95 px-4 py-4 text-sm leading-6 ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";
const SHEET_TEXTAREA_CLASS_NAME =
  "min-h-[10rem] w-full resize-none rounded-none border-0 border-b border-[#e4d8ca] bg-transparent px-0 pb-4 pt-2 text-[1.05rem] leading-8 text-ink outline-none transition placeholder:text-[#b7aa9a] focus:border-clay/45";

type ProjectImageSettingsSectionProps = {
  busy: boolean;
  creditCost: number;
  draft: ProjectImageSettingsDraft;
  onAddReference: (file: File) => Promise<void>;
  onDraftChange: (draft: Partial<ProjectImageSettingsDraft>) => void;
  onRemoveReference: (referencePath: string) => Promise<void>;
  onReorderReferences: (orderedReferencePaths: string[]) => Promise<void>;
  onReplaceReference: (referencePath: string, file: File) => Promise<void>;
  onSave: () => Promise<void> | void;
  savedSettings: ProjectImageSettingsView | null;
  status: "idle" | "loading" | "ready" | "error";
  submitError?: string | null;
  variant?: "card" | "sheet";
};

export function ProjectImageSettingsSection({
  busy,
  creditCost,
  draft,
  onAddReference,
  onDraftChange,
  onRemoveReference,
  onReorderReferences,
  onReplaceReference,
  onSave,
  savedSettings,
  status,
  submitError = null,
  variant = "card",
}: ProjectImageSettingsSectionProps) {
  const { t } = useTranslation();
  const isSheetVariant = variant === "sheet";
  const references = savedSettings?.artstyleReferences ?? [];
  const maxReferences = savedSettings?.maxReferences ?? 5;
  const generationReady = references.length > 0;
  const isLoading = status === "loading" && !savedSettings;

  return (
    <section
      className={cn(
        "space-y-6",
        isSheetVariant
          ? ""
          : "rounded-[32px] border border-white/70 bg-card/95 p-6 shadow-[0_26px_90px_-42px_rgba(38,28,18,0.5)]",
      )}
    >
      <div
        className={cn(
          "flex flex-wrap items-start justify-between gap-4",
          isSheetVariant && "border-b border-ink/10 pb-6",
        )}
      >
        <div className="max-w-3xl space-y-2">
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">
            {t("projects.image.eyebrow")}
          </p>
          <h2 className="text-3xl text-foreground">{t("projects.image.title")}</h2>
          <p className="leading-7 text-muted-foreground">{t("projects.image.body")}</p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={generationReady ? "secondary" : "outline"}>
            {generationReady
              ? t("projects.image.status.ready")
              : t("projects.image.status.missing")}
          </Badge>
          <Badge variant="outline">
            {t("projects.image.creditCost", { count: creditCost })}
          </Badge>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.45fr)_minmax(18rem,0.9fr)]">
        <div
          className={cn(
            "p-0",
            isSheetVariant
              ? ""
              : "rounded-[28px] border border-border/70 bg-background/75 p-5",
          )}
        >
          <div className={cn("space-y-2", isSheetVariant && "pr-6")}>
            <Label htmlFor="project-style-description">
              {t("projects.image.styleDescription.label")}
            </Label>
            <textarea
              className={isSheetVariant ? SHEET_TEXTAREA_CLASS_NAME : TEXTAREA_CLASS_NAME}
              id="project-style-description"
              onChange={(event) => onDraftChange({ styleDescription: event.target.value })}
              placeholder={t("projects.image.styleDescription.placeholder")}
              value={draft.styleDescription}
            />
            <p className="text-sm leading-6 text-muted-foreground">
              {t("projects.image.styleDescription.help")}
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <div
            className={
              generationReady
                ? "rounded-[28px] border border-emerald-500/20 bg-emerald-50/75 p-5 text-emerald-950"
                : "rounded-[28px] border border-dashed border-amber-500/35 bg-amber-50/85 p-5 text-amber-950"
            }
            role={generationReady ? "status" : "note"}
          >
            <div className="flex items-start gap-3">
              <Sparkles className="mt-1 h-4 w-4 shrink-0" />
              <div className="space-y-1">
                <p className="font-semibold">
                  {generationReady
                    ? t("projects.image.availability.readyTitle")
                    : t("projects.image.availability.missingTitle")}
                </p>
                <p className="text-sm leading-6">
                  {generationReady
                    ? t("projects.image.availability.readyBody", {
                        count: references.length,
                      })
                    : t("projects.image.availability.missingBody")}
                </p>
              </div>
            </div>
          </div>

          <div
            className={cn(
              "",
              isSheetVariant
                ? "border-t border-ink/10 pt-4"
                : "rounded-[28px] border border-border/70 bg-secondary/25 p-5",
            )}
          >
            <div className="flex items-start gap-3">
              <SwatchBook className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="space-y-1">
                <p className="font-semibold text-foreground">
                  {t("projects.image.notes.title")}
                </p>
                <p className="text-sm leading-6 text-muted-foreground">
                  {t("projects.image.notes.body")}
                </p>
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className="flex items-center gap-3 rounded-[24px] border border-border/70 bg-background/80 px-4 py-4 text-sm text-muted-foreground">
              <LoaderCircle className="h-4 w-4 animate-spin" />
              {t("projects.image.loading")}
            </div>
          ) : null}

          {status === "error" && !savedSettings ? (
            <div
              className="flex items-start gap-3 rounded-[24px] border border-destructive/20 bg-destructive/10 px-4 py-4 text-sm text-destructive"
              role="alert"
            >
              <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
              <p>{t("projects.image.loadError")}</p>
            </div>
          ) : null}
        </div>
      </div>

      <ArtstyleReferenceManager
        busy={busy}
        maxReferences={maxReferences}
        onAddReference={onAddReference}
        onRemoveReference={onRemoveReference}
        onReorderReferences={onReorderReferences}
        onReplaceReference={onReplaceReference}
        references={references}
        variant={variant}
      />

      {submitError ? <FormMessage>{submitError}</FormMessage> : null}

      <div
        className={cn(
          "flex flex-wrap justify-end gap-3",
          isSheetVariant && "border-t border-ink/10 pt-6",
        )}
      >
        <Button disabled={busy || isLoading} onClick={() => void onSave()} type="button">
          {busy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : null}
          {busy ? t("projects.image.actions.saving") : t("projects.image.actions.save")}
        </Button>
      </div>
    </section>
  );
}
